// STUDENT → readiness evidence → topic weaknesses/strengths → recommendations
// → mistakes (final drill-down level). Reuses the EXACT student-facing
// components (DiagnosticIntelligenceSection, RecommendationCard) against
// admin-scoped endpoints that wrap the same services — "admin views must
// aggregate the same intelligence the student sees" down to the component
// and response-shape level, not just the numbers.
import React, { useEffect, useState } from 'react';
import Icon from '../../components/ui/Icon';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import API from '../../services/api';
import DiagnosticIntelligenceSection from '../../components/diagnostic/DiagnosticIntelligenceSection';
import RecommendationCard from '../../components/recommendations/RecommendationCard';
import AptitudeAnalytics from '../../components/aptitude/AptitudeAnalytics';
import { readinessLevelColor, readinessLevelLabel } from '../../components/diagnostic/diagnosticHelpers';
import { SkeletonCard } from '../../components/ui/Skeleton';
import '../admin/Analytics.css';
import './BatchDrilldown.css';

const StudentIntelligence = () => {
  const { id } = useParams();
  const [params] = useSearchParams();
  const fromBatch = params.get('from');

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [profile, setProfile] = useState(null);
  const [readiness, setReadiness] = useState(null);
  const [diagnostic, setDiagnostic] = useState(null);
  const [recommendations, setRecommendations] = useState(null);
  const [aptitude, setAptitude] = useState(null);
  // Intervention status has no dedicated per-student endpoint — only the
  // batch-level rollup (/reports/batches/:id/intervention) computes it, so
  // this is only knowable when arriving from a batch's intervention/student
  // list (fromBatch set). Reuses that exact batch computation (find this
  // student in it) rather than re-deriving intervention criteria here.
  const [interventionEntry, setInterventionEntry] = useState(undefined); // undefined = unknown (no batch context), null = known-not-flagged

  useEffect(() => {
    (async () => {
      setLoading(true); setError('');
      try {
        const calls = [
          API.get(`/reports/student/${id}`),
          API.get(`/reports/students/${id}/readiness`),
          API.get(`/reports/students/${id}/diagnostic-intelligence`),
          API.get(`/reports/students/${id}/recommendations`),
          API.get(`/reports/student/${id}/aptitude`),
        ];
        if (fromBatch) calls.push(API.get(`/reports/batches/${fromBatch}/intervention`));

        const results = await Promise.allSettled(calls);
        const [perf, r, d, rec, apt, iv] = results;
        if (perf.status === 'fulfilled') setProfile(perf.value.data.student_profile);
        if (r.status === 'fulfilled') setReadiness(r.value.data);
        if (d.status === 'fulfilled') setDiagnostic(d.value.data);
        if (rec.status === 'fulfilled') setRecommendations(rec.value.data.recommendations || []);
        if (apt.status === 'fulfilled') setAptitude(apt.value.data);
        if (fromBatch && iv?.status === 'fulfilled') {
          setInterventionEntry(iv.value.data.students.find(s => s.student_id === id) || null);
        }
        if (perf.status === 'rejected') throw perf.reason;
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to load student intelligence');
      } finally { setLoading(false); }
    })();
  }, [id, fromBatch]);

  if (loading) {
    return (
      <div className="axh-page">
        <SkeletonCard lines={2} />
        <SkeletonCard lines={4} />
        <SkeletonCard lines={3} />
      </div>
    );
  }
  if (error) return <div className="axh-page"><div className="axh-error">{error}</div></div>;

  return (
    <div className="axh-page">
      <div className="axh-topbar">
        <div>
          <Link to={fromBatch ? `/admin/batches/${fromBatch}/analytics` : '/admin/batches-overview'} className="bdx-breadcrumb">
            <Icon name="chevron-left" size={14} /> {fromBatch ? 'Back to Batch' : 'Back to Batches'}
          </Link>
          <h1>{profile?.name}</h1>
          <p>{profile?.roll_number} · {profile?.branch}</p>
        </div>
      </div>

      <section className="axh-section">
        <div className="axh-section-head"><h2>Readiness</h2></div>
        <div className="axh-card">
          {readiness?.available ? (
            <div className="bdx-readiness-card" style={{ borderColor: readinessLevelColor(readiness.level) }}>
              <div className="bdx-readiness-score" style={{ color: readinessLevelColor(readiness.level) }}>
                {readiness.score != null ? Number(readiness.score).toFixed(1) : '—'}
              </div>
              <div>
                <div className="bdx-readiness-level" style={{ color: readinessLevelColor(readiness.level) }}>{readinessLevelLabel(readiness.level)}</div>
                <div className="bdx-readiness-sub">
                  {readiness.confidence} confidence · {readiness.considered} attempt{readiness.considered === 1 ? '' : 's'} considered · source: {readiness.source}
                  {readiness.trend !== 'insufficient_data' && ` · trend: ${readiness.trend}`}
                </div>
              </div>
            </div>
          ) : (
            <div className="axh-empty">{readiness?.message || 'Not enough assessment activity yet to measure placement readiness.'}</div>
          )}
        </div>
      </section>

      {/* Intervention signal — a distinct verdict from readiness/diagnostic
          evidence below, computed once by the batch's own intervention
          rollup and just looked up here, never re-derived. Only shown when
          we arrived with batch context (no per-student intervention
          endpoint exists standalone) — omitted rather than guessed at
          otherwise, so this is never mistaken for "not flagged". */}
      {fromBatch && interventionEntry !== undefined && (
        <section className="axh-section">
          <div className="axh-section-head"><h2>Intervention Signal</h2></div>
          <div className="axh-card">
            {interventionEntry ? (
              <div className="bdx-intervention-row" style={{ cursor: 'default' }}>
                <div className="bdx-intervention-top">
                  <span className="name-cell" style={{ cursor: 'default' }}>Flagged for intervention</span>
                  <span className="axh-badge" style={{ background: 'var(--z-danger-tint)', color: 'var(--z-danger)' }}>
                    severity {interventionEntry.severity_score != null ? interventionEntry.severity_score.toFixed(2) : '—'}
                  </span>
                </div>
                <ul>{interventionEntry.reasons.map((r, i) => <li key={i}>{r}</li>)}</ul>
              </div>
            ) : (
              <div className="axh-empty">Not currently flagged for intervention in this batch.</div>
            )}
          </div>
        </section>
      )}

      <DiagnosticIntelligenceSection data={diagnostic} />

      {/* Historical trend — a distinct dimension from the point-in-time
          readiness/diagnostic sections above: the same continuous
          Aptitude Analytics module the student sees on their own Progress
          page, fed by the existing per-student aptitude endpoint. */}
      {aptitude && (aptitude.history || []).length > 0 && (
        <section className="axh-section">
          <div className="axh-section-head"><h2>Historical Trend</h2></div>
          <AptitudeAnalytics
            profile={aptitude.aptitude_profile || null}
            history={aptitude.history || []}
            trend={aptitude.trend || []}
            subtitle={`${profile?.name || 'This student'}'s readiness trend across every aptitude attempt.`}
            defaultTab="timeline"
          />
        </section>
      )}

      <section className="axh-section">
        <div className="axh-section-head"><h2>Recommendations</h2><p>Read-only — the same recommendation engine output the student sees, without the ability to start a session on their behalf.</p></div>
        {recommendations.length === 0 ? (
          <div className="axh-card"><div className="axh-empty">No recommendations available.</div></div>
        ) : (
          <div className="bdx-rec-grid">
            {recommendations.map((r, i) => (
              <RecommendationCard key={`${r.type}-${r.target?.topic_id || r.target?.kind || i}`} rec={r} variant="compact" readOnly />
            ))}
          </div>
        )}
      </section>
    </div>
  );
};

export default StudentIntelligence;
